<template>
    <div class="d-flex flex-wrap-none">
        <UserManagementNav active-component="users" />
        <div class="admin-content" role="main" aria-labelledby="UsersHeading">
            <LoadingContainer :promise="loadingPromise">
                <div class="float-right">
                    <button class="btn btn-primary btn-sm" @click.prevent="showFederationCreateModal">New User</button>
                </div>
                <h1 class="admin-content-heading" id="UsersHeading">Users</h1>
                <div class="d-flex mb-2">
                    <ul class="nav nav-tabs mb-0">
                        <li class="nav-item">
                            <a class="nav-link" tabindex="0" id="formFormsTab" data-toggle="tab" :class="{ active: tab == 'active-users' }" @click.prevent="changeTab('active-users')"> Active </a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" tabindex="0" id="contactFormsTab" :class="{ active: tab == 'invited-users' }" @click.prevent="changeTab('invited-users')">
                                Invites
                                <span class="badge badge-light ml-1" v-show="federationRequests.length > 0">{{ federationRequests.length }}</span>
                            </a>
                        </li>
                    </ul>
                    <form class="form-inline ml-3" @submit.prevent>
                        <label for="user_filter" class="sr-only">Filter:</label>
                        <input type="search" class="form-control form-control-sm" placeholder="Filter..." id="user_filter" v-model="filterText" />
                    </form>
                </div>
                <div v-show="tab == 'active-users'" role="region" aria-label="Active Users">
                    <VueDataTable :headers="activeUserColumns" :items="filteredActiveUsers" sort-by="FirstName" sort-type="desc" table-class-name="vue-data-table table-sm" theme-color="#3f51b5" must-sort hide-footer :rows-per-page="100000" :prevent-context-menu-row="false">
                        <!-- Headers -->
                        <template #header-icon>
                            <span class="sr-only">Type</span>
                        </template>
                        <template #header-action>
                            <span class="sr-only">Action</span>
                        </template>

                        <!-- Rows -->
                        <!-- eslint-disable-next-line vue/no-unused-vars -->
                        <template #body="{ key, ...items }">
                            <tbody>
                                <tr v-for="user in Object.values(userDtoCast(items))">
                                    <td class="icon icon-user"><span class="sr-only">User</span></td>
                                    <td v-if="useDisplayName">{{ effectiveDisplayName(user) }}</td>
                                    <template v-else>
                                        <td>{{ user.FirstName }}</td>
                                        <td>{{ user.LastName }}</td>
                                    </template>
                                    <td>{{ user.UserName }}</td>
                                    <td>{{ user.Email }}</td>
                                    <td class="text-right nowrap">
                                        <button class="passthrough icon icon-settings" title="Settings" aria-label="Settings" @click.prevent="showEditModal(user, $event)" />
                                        <button class="passthrough icon icon-trash" title="Delete" aria-label="Delete" @click.prevent="deleteUser(user)" />
                                        <AsyncOperationStatus :promise="deleteUserPromises[user.UserToken]" />
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </VueDataTable>
                </div>
                <div v-show="tab == 'invited-users'" role="region" aria-label="Invited Users">
                    <VueDataTable :headers="activeUserColumns" :items="flattenedFilteredFederationRequests" sort-by="FirstName" sort-type="asc" table-class-name="vue-data-table table-sm" theme-color="#3f51b5" must-sort hide-footer :rows-per-page="100000" :prevent-context-menu-row="false">
                        <!-- Headers -->
                        <template #header-icon>
                            <span class="sr-only">Type</span>
                        </template>
                        <template #header-action>
                            <span class="sr-only">Action</span>
                        </template>

                        <!-- Rows -->
                        <!-- eslint-disable-next-line vue/no-unused-vars -->
                        <template #body="{ key, ...items }">
                            <tbody>
                                <tr v-for="federationRequest in Object.values(finalFederationRequestDtoCast(items))">
                                    <td class="icon icon-user"></td>
                                    <td>{{ federationRequest.FirstName }}</td>
                                    <td>{{ federationRequest.LastName }}</td>
                                    <td>{{ federationRequest.Email }}</td>
                                    <td class="nowrap">{{ localeDateTime(federationRequest.Expires) }}</td>
                                    <td class="text-right nowrap">
                                        <AsyncOperationStatus :promise="resendPromises[federationRequest.FederationToken]" />
                                        <button class="passthrough icon icon-mail ml-1" title="Re-invite" aria-label="Re-invite" @click.prevent="resendFederationRequestInvite(federationRequest.unflattened)" />
                                        <button class="passthrough icon icon-settings" title="Settings" aria-label="Settings" @click.prevent="showFederationEditModal(federationRequest.unflattened, $event)" />
                                        <button class="passthrough icon icon-trash" title="Delete" aria-label="Delete" @click.prevent="deleteFederationRequest(federationRequest.unflattened)" />
                                        <AsyncOperationStatus :promise="deletePromises[federationRequest.FederationToken]" />
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </VueDataTable>
                </div>
            </LoadingContainer>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, inject, onActivated, onDeactivated } from "vue";
import { throwIfNonSuccessStatus, fetchXsrf } from "@/lib/fetchHelpers.3";
import { localeDateTime } from "@/lib/localeDatetime.3";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import LoadingContainer from "@/lib/LoadingContainer.3.vue";
import getData from "../../lib/getData";
import UserSettingModal from "./UserSettingModal.3.vue";
import UserCreationModal from "./UserCreationModal.3.vue";
import FederationRequestEditModal from "./FederationRequestEditModal.3.vue";
import UserManagementNav from "./UserManagementNav.3.vue";
import "vue3-easy-data-table/dist/style.css";
import VueDataTable, { Header } from "vue3-easy-data-table";
// import { asyncComputed } from "@vueuse/core";
import { useMainStore } from "@/mainStore";
import { showModalInjectKey } from "@/AppShell.3.vue.InjectKeys";
import { UserDto } from "@cs/UserDto";
import { ContainerDto } from "@cs/ContainerDto";
import { UserStatusDto } from "@cs/UserStatusDto";
import { FederationRequestDto } from "@cs/FederationRequestDto";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
import { UserAndFederateRequest } from "@cs/UserAndFederateRequest";

type FinalFederationRequestDto = FederationRequestDto & UserDto & { unflattened: FederationRequestDto };

export default defineComponent({
    components: {
        AsyncOperationStatus,
        VueDataTable,
        LoadingContainer,
        UserManagementNav,
    },
    setup() {
        const mainStore = useMainStore();
        const showModal = inject(showModalInjectKey)!;

        const loadingPromise = ref<Promise<void>>(null!);
        const users = ref<UserDto[]>([]);
        const federationRequests = ref<FederationRequestDto[]>([]);
        const containerDataPromise = ref<Promise<ContainerDto>>(null!);
        const deleteUserPromises = ref<Record<string, Promise<void>>>({});
        const deletePromises = ref<Record<string, Promise<void>>>({});
        const resendPromises = ref<Record<string, Promise<void>>>({});
        const filterText = ref<string>(null!);
        const tab = ref("active-users");
        const useDisplayName = ref(false);

        const activeUserColumns = computed((): Header[] => {
            const usernameColumn = tab.value == "active-users" ? { text: "Username", value: "UserName", sortable: true } : null;
            const expireColumn = tab.value == "invited-users" ? { text: "Expires", value: "Expires", sortable: true } : null;
            const columns = [
                {
                    text: "",
                    value: "icon",
                    sortable: false,
                    width: 30,
                },
                ...(useDisplayName.value && tab.value == "active-users"
                    ? [
                          {
                              text: "Name",
                              value: "DisplayName",
                              sortable: true,
                          },
                      ]
                    : [
                          {
                              text: "First",
                              value: "FirstName",
                              sortable: true,
                          },
                          {
                              text: "Last",
                              value: "LastName",
                              sortable: true,
                          },
                      ]),
                usernameColumn!,
                {
                    text: "Email",
                    value: "Email",
                    sortable: true,
                },
                expireColumn!,
                {
                    text: "",
                    value: "action",
                    sortable: false,
                    width: 1,
                },
            ];
            return columns.filter(i => i != null);
        });
        const activeUsers = computed(() => {
            return users.value.filter(i => i.Status == UserStatusDto.Active);
        });
        const filteredActiveUsers = computed(() => {
            const filterFunc = filterUserFunc();
            return activeUsers.value.filter(filterFunc);
        });
        // const inactiveUsers = computed(() => {
        //     return users.value.filter(i => i.Status == UserStatusDto.Inactive);
        // });
        // const filteredInactiveUsers = computed(() => {
        //     const filterFunc = filterUserFunc();
        //     return inactiveUsers.value.filter(filterFunc);
        // });
        const filteredFederationRequests = computed(() => {
            const filterFunc = filterFederationRequestFunc();
            return federationRequests.value.filter(filterFunc);
        });
        const flattenedFilteredFederationRequests = computed(() => {
            return filteredFederationRequests.value.map(i => ({ ...i, ...i.User, unflattened: i }) as FinalFederationRequestDto);
        });

        const effectiveDisplayName = (user: UserDto) => {
            if (user.DisplayName != null && !/^\s*$/.test(user.DisplayName)) {
                return user.DisplayName;
            } else {
                return `${user.FirstName ?? ""} ${user.LastName ?? ""}`;
            }
        };

        // const adminUrl = asyncComputed(() => {
        //     return mainStore.getUrl("admin");
        // });

        const filterUserFunc = () => {
            const _filterText = filterText.value;
            if (_filterText == null || /^\s*$/.test(_filterText)) {
                return () => true;
            }
            const lowerFilterText = _filterText.toLowerCase();
            const test = (i: string) => i != null && i.toLowerCase().includes(lowerFilterText);
            return (user: UserDto) => test(user.FirstName) || test(user.LastName) || test(user.UserName) || test(user.Email);
        };
        const filterFederationRequestFunc = () => {
            const func = filterUserFunc();
            return (federationRequest: FederationRequestDto) => func(federationRequest.User);
        };
        const init = async (refreshCache: boolean) => {
            useDisplayName.value = (await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.DisplayNameForUsers.Id)) ?? false;
            const usersAndFederationRequests = await getData<UserAndFederateRequest>(mainStore, refreshCache ? null : "AdminUserManagementUsers", () => mainStore.getUrl("adminUserManagementUsersData"));
            users.value = usersAndFederationRequests.Users ?? [];
            federationRequests.value = usersAndFederationRequests.FederationRequests ?? [];

            containerDataPromise.value = fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersContainerData")).then(r => {
                throwIfNonSuccessStatus(r);
                return r.json();
            });
        };
        const showEditModal = async (user: UserDto, event: Event) => {
            const fullUser: UserDto = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersUserData", { userToken: user.UserToken })).then(r => {
                throwIfNonSuccessStatus(r);
                return r.json();
            });
            showModal(UserSettingModal, {
                userInput: fullUser,
                containerData: await containerDataPromise.value,
                savingCallback: (user: UserDto) => {
                    const index = users.value.findIndex(i => i.UserToken == user.UserToken);
                    users.value.splice(index, 1, user);
                },
                triggerElement: event.target,
            });
        };
        const deleteUser = async (user: UserDto) => {
            if (!confirm("Delete this user?")) {
                return;
            }
            deleteUserPromises.value[user.UserToken] = (async () => {
                const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersDelete"), {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(user.UserToken),
                });
                throwIfNonSuccessStatus(response);
                const index = users.value.findIndex(i => i.UserToken == user.UserToken);
                users.value.splice(index, 1);
            })();
        };
        const showFederationEditModal = async (federationRequest: FederationRequestDto, event: Event) => {
            showModal(FederationRequestEditModal, {
                federationRequest: federationRequest,
                containerData: await containerDataPromise.value,
                savingCallback: (federationRequest: FederationRequestDto) => {
                    const index = federationRequests.value.findIndex(i => i.FederationToken == federationRequest.FederationToken);
                    federationRequests.value.splice(index, 1, federationRequest);
                },
                triggerElement: event.target,
            });
        };
        const deleteFederationRequest = async (federationRequest: FederationRequestDto) => {
            if (!confirm("Delete this user?")) {
                return;
            }
            deletePromises.value[federationRequest.FederationToken] = (async () => {
                const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersDeleteFederation"), {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(federationRequest.FederationToken),
                });
                throwIfNonSuccessStatus(response);
                const index = federationRequests.value.findIndex(i => i.FederationToken == federationRequest.FederationToken);
                federationRequests.value.splice(index, 1);
            })();
        };
        const resendFederationRequestInvite = async (federationRequest: FederationRequestDto) => {
            if (!confirm("Resend this user's invite?")) {
                return;
            }
            resendPromises.value[federationRequest.FederationToken] = (async () => {
                const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminUserManagementUsersResentFederationInvite"), {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(federationRequest.FederationToken),
                });
                throwIfNonSuccessStatus(response);
                const index = federationRequests.value.findIndex(i => i == federationRequest);
                federationRequests.value.splice(index, 1, await response.json());
            })();
        };
        const showFederationCreateModal = async (event: Event) => {
            showModal(UserCreationModal, {
                containerData: await containerDataPromise.value,
                savingCallback: (federationRequest: FederationRequestDto) => {
                    federationRequests.value.push(federationRequest);
                    tab.value = "invited-users";
                },
                triggerElement: event.target,
            });
        };
        const changeTab = (_tab: string) => {
            tab.value = _tab;
        };

        onActivated(() => {
            if (loadingPromise.value == null) {
                loadingPromise.value = init(true);
            }
        });
        onDeactivated(() => {
            loadingPromise.value = null!;
        });

        loadingPromise.value = init(false);

        return {
            // States
            loadingPromise,
            deleteUserPromises,
            deletePromises,
            resendPromises,
            federationRequests,
            filterText,
            tab,

            // Computed
            activeUserColumns,
            filteredActiveUsers,
            flattenedFilteredFederationRequests,
            useDisplayName,

            // Methods
            showEditModal,
            deleteUser,
            showFederationEditModal,
            deleteFederationRequest,
            resendFederationRequestInvite,
            showFederationCreateModal,
            changeTab,

            effectiveDisplayName,
            localeDateTime,

            // Vue-tsc workarounds
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
            userDtoCast: (i: any[]) => i as UserDto[],
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
            finalFederationRequestDtoCast: (i: any[]) => i as FinalFederationRequestDto[],
        };
    },
});
</script>
